// The source-shader registry, free of bundler APIs so the settings core can read it: `index.js` discovers the modules and registers them here.
import { isSubset, PIPELINE, prepareShader } from './common'

const logger = console.scope('shaders')

export const SHADER_PREFIX = 'shader:'

// On globalThis so a dev hot update re-running this module or `index.js` keeps importers' bindings and listeners live.
globalThis.__oyloShaderRegistry ??= {
	shaders: {},
	names: [],
	sources: new Map(),
	listeners: new Set(),
	version: 0,
}
const registry = globalThis.__oyloShaderRegistry
const SHADERS = registry.shaders

export const SHADER_NAMES = registry.names

/** Registers `{ [file]: module }`, replacing the previous set. A re-registration returns, and tells listeners, every name it added, changed or removed. */
export const registerShaders = modules => {
	const previous = { ...SHADERS }
	const isReplacing = Object.keys(previous).length > 0
	const sources = new Map()
	for (const key of Object.keys(SHADERS)) delete SHADERS[key]
	for (const [file, mod] of Object.entries(modules)) {
		const shader = mod.default
		if (!shader?.name || !(isSubset(shader) || (shader.fragment && shader.params))) {
			logger.warn('ignoring malformed shader module', file)
			continue
		}
		if (SHADERS[shader.name]) logger.warn('duplicate shader name', shader.name, file)
		sources.set(shader.name, shader)
		// A pipeline edit rebuilds every subset: its fragment is the pipeline's.
		const kept =
			registry.sources.get(shader.name) === shader && !(isSubset(shader) && registry.pipeline !== PIPELINE.source)
		try {
			SHADERS[shader.name] = kept ? previous[shader.name] : prepareShader(shader)
		} catch (e) {
			logger.warn('ignoring shader module that does not prepare', file, e)
			sources.delete(shader.name)
		}
	}
	const changed = [...new Set([...Object.keys(previous), ...sources.keys()])].filter(
		name =>
			registry.sources.get(name) !== sources.get(name) ||
			(isSubset(sources.get(name)) && registry.pipeline !== PIPELINE.source)
	)
	registry.sources = sources
	registry.pipeline = PIPELINE.source
	SHADER_NAMES.splice(0, SHADER_NAMES.length, ...Object.keys(SHADERS))
	if (!isReplacing || !changed.length) return []
	registry.version++
	for (const listener of registry.listeners) listener(changed)
	return changed
}

/** Calls `listener(names)` after a re-registration adds, changes or removes those shaders. Returns the unsubscribe. */
export const onShadersChanged = listener => {
	registry.listeners.add(listener)
	return () => registry.listeners.delete(listener)
}

/** Counts re-registrations that changed something: a `useSyncExternalStore` snapshot beside `onShadersChanged`. */
export const shaderRegistryVersion = () => registry.version

/** True when a source id names a shader rather than a device or file. */
export const isShaderSource = source => typeof source === 'string' && source.startsWith(SHADER_PREFIX)

/** `'shader:voxel_tunnel'` (or bare `'voxel_tunnel'`) → the registry entry, or undefined. */
export const getShader = source =>
	isShaderSource(source) ? SHADERS[source.slice(SHADER_PREFIX.length)] : SHADERS[source]

// The subset pairs a fade walks on a path, keyed by both names sorted; every other pair of two subsets crossfades.
const PATHED = new Set(['grid|stripes', 'plasma|stripes', 'ribbons|stripes', 'cube_tunnel|voxel_tunnel'])

/** True when a fade between two subsets, by name, walks a path rather than crossfading. The rig sets `window.__rigAllPaths` to walk every pair's path. */
export const isPathed = (a, b) => !!globalThis.__rigAllPaths || PATHED.has([a, b].sort().join('|'))

// The subsets a user can pick; the rest are dev UI only (`SHOW_DEV_UI`).
const SHIPPED = new Set(['stripes', 'grid', 'plasma', 'ribbons', 'cube_tunnel', 'voxel_tunnel'])

/** True when a shader, by name, is in the user's pickers. */
export const isShipped = name => SHIPPED.has(name)

/** Default uniform values for a shader, by name or `shader:`-prefixed source. */
export const getShaderDefaults = source => {
	const shader = getShader(source)
	if (!shader) return {}
	const out = {}
	for (const [key, def] of Object.entries(shader.params)) out[key] = def.initial
	return out
}

export default SHADERS
